import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCartIcon, HeartIcon } from 'lucide-react';
import { Product } from '../../types';
import { Badge, getBadgeVariant } from '../ui/Badge';
import { Rating } from '../ui/Rating';
import { useCart } from '../../context/CartContext';
interface ProductCardProps {
  product: Product;
  className?: string;
}
export function ProductCard({
  product,
  className = ''
}: ProductCardProps) {
  const {
    addToCart
  } = useCart();
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('uk-UA').format(price);
  };
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
  };
  return <motion.article whileHover={{
    y: -4
  }} transition={{
    duration: 0.2
  }} className={`group bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden flex flex-col h-full ${className}`} data-id="element-260">
      <Link to={`/product/${product.id}`} className="block flex-1 flex flex-col" data-id="element-261">
        {/* Image Container - Placeholder instead of real image */}
        <div className="relative aspect-square overflow-hidden bg-slate-200 flex-shrink-0 flex items-center justify-center" data-id="element-262">
          <div className="text-center p-6" data-id="element-263">
            <div className="w-20 h-20 bg-slate-300 rounded-full mx-auto mb-3" data-id="element-264"></div>
            <p className="text-sm text-slate-500" data-id="element-265">Зображення товару</p>
          </div>

          {/* Badge */}
          {product.badge && <div className="absolute top-3 left-3" data-id="element-266">
              <Badge variant={getBadgeVariant(product.badge)} data-id="element-267">
                {product.badge}
              </Badge>
            </div>}

          {/* Quick Actions */}
          <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-268">
            <motion.button whileTap={{
            scale: 0.9
          }} className="p-2 bg-white rounded-full shadow-md hover:bg-slate-50 transition-colors" aria-label="Додати в обране" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }} data-id="element-269">
              <HeartIcon className="w-5 h-5 text-slate-600" data-id="element-270" />
            </motion.button>
          </div>

          {/* Out of Stock Overlay */}
          {!product.inStock && <div className="absolute inset-0 bg-white/80 flex items-center justify-center" data-id="element-271">
              <span className="px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg" data-id="element-272">
                Немає в наявності
              </span>
            </div>}
        </div>

        {/* Content - Fixed height structure */}
        <div className="p-4 flex flex-col flex-1" data-id="element-273">
          {/* Rating - Fixed height */}
          <div className="mb-2 h-5" data-id="element-274">
            <Rating value={product.rating} reviewCount={product.reviewCount} size="sm" data-id="element-275" />
          </div>

          {/* Name - Fixed 2 lines */}
          <h3 className="text-sm font-medium text-slate-800 mb-3 group-hover:text-sky-600 transition-colors flex-1 line-clamp-2 min-h-[2.5rem]" data-id="element-276">
            {product.name}
          </h3>

          {/* Price & Add to Cart - Fixed at bottom */}
          <div className="mt-auto" data-id="element-277">
            <div className="flex items-baseline gap-2 mb-2" data-id="element-278">
              <span className="text-lg font-bold text-slate-900" data-id="element-279">
                {formatPrice(product.price)} ₴
              </span>
              {product.oldPrice && <span className="text-sm text-slate-400 line-through" data-id="element-280">
                  {formatPrice(product.oldPrice)} ₴
                </span>}
            </div>

            <div className="flex items-center justify-between gap-2" data-id="element-281">
              {product.inStock && <span className="text-xs text-emerald-600 font-medium" data-id="element-282">
                  В наявності
                </span>}

              {product.inStock && <motion.button whileTap={{
              scale: 0.9
            }} onClick={handleAddToCart} className="ml-auto p-2.5 bg-sky-500 text-white rounded-xl hover:bg-sky-600 transition-colors shadow-md" aria-label="Додати в кошик" data-id="element-283">
                  <ShoppingCartIcon className="w-5 h-5" data-id="element-284" />
                </motion.button>}
            </div>
          </div>
        </div>
      </Link>
    </motion.article>;
}